import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { v2URL } from '../../../../../../../../shared/constants/urls.constants';
import { RiskRegisterService } from '../risk-register.service';
import { Risk } from '../risk-register.models';
import { RESPONSE_TYPE_LABEL, serverErrorMessage } from '../risk-register.util';

/** Same rule as Validators.required but under another error key, so the shared fields do not draw a "*" on the label. */
const needed = (c: AbstractControl): ValidationErrors | null => (c.value == null || String(c.value).trim() === '' ? { needed: true } : null);

/** "It happened": file an Issue from the risk (spec §3.3). */
@Component({
  selector: 'app-risk-convert-dialog',
  templateUrl: './risk-convert-dialog.component.html',
  styleUrls: ['./risk-convert-dialog.component.scss'],
  standalone: false,
})
export class RiskConvertDialogComponent implements OnInit, OnDestroy {
  form = new FormGroup({
    type: new FormControl<string>(null, needed),
    title: new FormControl('', [needed, Validators.maxLength(1024)]),
    description: new FormControl(''),
  });
  typeOptions: { value: string; label: string }[] = [];
  loadingTypes = true;
  saving = false;
  private readonly onDestroy = new Subject<void>();

  readonly dialogRef = inject<MatDialogRef<RiskConvertDialogComponent>>(MatDialogRef);
  readonly data = inject<{ risk: Risk; projectId: string }>(MAT_DIALOG_DATA);
  private readonly riskService = inject(RiskRegisterService);
  private readonly commonService = inject(CommonService);

  ngOnInit(): void {
    const r = this.data.risk;
    const strategy = r.response_type ? `Strategy was ${(RESPONSE_TYPE_LABEL[r.response_type] || r.response_type).toLowerCase()}: ${r.response_strategy || ''}`.trim() : (r.response_strategy || '');
    this.form.patchValue({
      title: `${r.description} (from Risk ${r.risk_no})`.slice(0, 1024),
      description: [`Risk ${r.risk_no}: ${r.description}`, r.project_impact ? `Project impact: ${r.project_impact}` : '', strategy].filter(Boolean).join('\n'),
    });
    // Project-scoped issue types (org types plus the project's own): the same source the issue form uses.
    const cfg = v2URL.issueTypes.fetch;
    this.commonService.callAPI(cfg.method, cfg.url({ projectId: this.data.projectId })).pipe(takeUntil(this.onDestroy)).subscribe({
      next: (resp: { data?: { type: string; label?: string }[] }) => {
        this.typeOptions = (resp?.data || []).map(t => ({ value: t.type, label: t.label || t.type }));
        if (this.typeOptions.length === 1) this.form.get('type').setValue(this.typeOptions[0].value);
        this.loadingTypes = false;
      },
      error: () => { this.typeOptions = []; this.loadingTypes = false; },
    });
  }

  convert(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;
    const v = this.form.value;
    this.riskService.convert(this.data.risk.id, { type: v.type, title: v.title.trim(), description: v.description?.trim() || undefined, internal: this.data.risk.internal })
      .pipe(takeUntil(this.onDestroy)).subscribe({
        next: risk => { this.saving = false; this.commonService.notification('Issue created and risk converted', 'success'); this.dialogRef.close(risk); },
        error: err => { this.saving = false; this.commonService.notification(serverErrorMessage(err?.error, 'Could not convert the risk'), 'error'); },
      });
  }

  ngOnDestroy(): void { this.onDestroy.next(); this.onDestroy.complete(); }
}
